import React from 'react';
import { Modal, Checkbox, Tooltip } from 'antd';
import { KdevIconFont } from '../../../KdevIconFont';
import { Bind } from 'lodash-decorators';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import {
    kwaipilot_system_close,
    common_system_drag02,
} from '@kid/enterprise-icon/icon/output/icons';
import classnames from 'classnames';
import css from './PageLayoutTopMenu.less';
import { ITopMenuItem, ITopMenuSection } from './types';

function MenuItem({ canDrag, item, onDelete }) {
    return (
        <div
            className={classnames(
                css.menuSortableItem,
                canDrag && css.menuSortableItemDrag
            )}
        >
            <div className={css.left}>
                <div
                    className={`${css.dragIcon} ${
                        canDrag ? 'kdev-page-v2-custom-menu-drag' : ''
                    }`}
                >
                    {canDrag ? (
                        <KdevIconFont id={common_system_drag02} />
                    ) : null}
                </div>
                <div className={css.image}>
                    <img src={item.iconUrl} />
                </div>
                <span className={css.text}>{item.name}</span>
            </div>

            <div className={css.right}>
                <div
                    className={css.deleteIcon}
                    onClick={(event) => {
                        onDelete?.(item);
                        event.stopPropagation();
                    }}
                >
                    {canDrag ? (
                        <KdevIconFont id={kwaipilot_system_close} />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

const SortableItem = SortableElement(({ item, onDelete }) => {
    return <MenuItem canDrag={true} item={item} onDelete={onDelete} />;
});

const SortableList = SortableContainer(({ items, onDelete }) => {
    return (
        <div>
            {items.map((item, index) => (
                <SortableItem
                    item={item}
                    key={item.webUrl}
                    index={index}
                    onDelete={onDelete}
                    disabled={item.fixed}
                />
            ))}
        </div>
    );
});

interface IProps {
    onSave: (list: any[]) => void;
}

interface IState {
    modalShow: boolean;
    allDataList: ITopMenuSection[];
    sortList: ITopMenuItem[];
    fixedList: ITopMenuItem[];
}

export class CustomTopMenu extends React.Component<IProps, IState> {
    state = {
        modalShow: false,
        allDataList: [] as ITopMenuSection[],
        sortList: [] as ITopMenuItem[],
        fixedList: [] as ITopMenuItem[],
    };

    @Bind
    public show(params: {
        allMenuList: ITopMenuSection[];
        topMenuList: ITopMenuItem[];
    }) {
        const fixedList = params.topMenuList.filter((item) => item.fixed);
        const sortList = params.topMenuList.filter((item) => !item.fixed);

        this.setState({
            modalShow: true,
            allDataList: params.allMenuList,
            fixedList,
            sortList,
        });
    }

    @Bind
    public hide() {
        this.setState({
            modalShow: false,
            allDataList: [],
            sortList: [],
        });
    }

    @Bind
    private onSortEnd({ oldIndex, newIndex }) {
        if (oldIndex !== newIndex) {
            const newIndexData = arrayMove(
                this.state.sortList,
                oldIndex,
                newIndex
            );
            this.setState({
                sortList: newIndexData,
            });
        }
    }

    @Bind
    private onDelete(item: ITopMenuItem) {
        this.deleteSortListItem(item);
    }

    @Bind
    private appendSortListItem(item: ITopMenuItem) {
        this.setState({
            sortList: [...this.state.sortList, item],
        });
    }

    @Bind
    private deleteSortListItem(item: ITopMenuItem) {
        const index = this.state.sortList.indexOf(item);

        if (index > -1) {
            this.state.sortList.splice(index, 1);

            this.setState({
                sortList: this.state.sortList,
            });
        }
    }

    @Bind
    renderLeftMenuBox() {
        return (
            <div className={css.leftMenuBox}>
                {this.state.allDataList.map((section: ITopMenuSection) => {
                    return (
                        <div className={css.sectionBox}>
                            <div className={css.sectionHeader}>
                                {section.groupName}
                            </div>
                            {section.children?.map((item: ITopMenuItem) => {
                                const isChecked = [
                                    ...this.state.fixedList,
                                    ...this.state.sortList,
                                ].find((menu) => item.webUrl === menu.webUrl);

                                let disabled = false;
                                let disabledPrompt = '';

                                if (
                                    this.state.sortList.length +
                                        this.state.fixedList.length >=
                                        7 &&
                                    !isChecked
                                ) {
                                    disabled = true;
                                    disabledPrompt = '最多只能选择7个模块';
                                }

                                if (section.third) {
                                    disabled = true;
                                    disabledPrompt = '第三方平台，不允许添加';
                                }

                                if (item.fixed) {
                                    disabled = true;
                                    disabledPrompt =
                                        item.name + '默认展示，不允许取消';
                                }

                                const checkboxEle = (
                                    <div>
                                        <Checkbox
                                            className={css.checkboxRow}
                                            checked={!!isChecked}
                                            onChange={() => {
                                                if (!isChecked) {
                                                    this.appendSortListItem(
                                                        item
                                                    );
                                                } else {
                                                    this.deleteSortListItem(
                                                        item
                                                    );
                                                }
                                            }}
                                            disabled={disabled}
                                        >
                                            <div className={css.checkboxLabel}>
                                                <div className={css.image}>
                                                    <img src={item.iconUrl} />
                                                </div>
                                                <span className={css.text}>
                                                    {item.name}
                                                </span>
                                            </div>
                                        </Checkbox>
                                    </div>
                                );

                                if (!disabled) {
                                    return checkboxEle;
                                }

                                return (
                                    <Tooltip
                                        placement="topLeft"
                                        title={disabledPrompt}
                                        trigger={['hover']}
                                    >
                                        {checkboxEle}
                                    </Tooltip>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        );
    }

    @Bind
    renderRightMenuBox() {
        const total =
            (this.state.sortList.length || 0) +
            (this.state.fixedList.length || 0);
        return (
            <div className={css.rightMenuBox}>
                <div className={css.selectedHeader}>
                    <div>已选 ({total})</div>
                </div>
                <div className={css.selectedArea}>
                    {this.state.fixedList.map((item) => (
                        <MenuItem
                            item={item}
                            canDrag={false}
                            onDelete={() => {}}
                        />
                    ))}
                    <SortableList
                        items={this.state.sortList}
                        onSortEnd={this.onSortEnd}
                        lockAxis="y"
                        onDelete={this.onDelete}
                        distance={10}
                        lockToContainerEdges={true}
                        helperClass={css.menuSortableItemDragHelper}
                    />
                </div>
            </div>
        );
    }

    render(): React.ReactNode {
        return (
            <Modal
                wrapClassName={css.customTopMenuBox}
                visible={this.state.modalShow}
                title={'自定义顶部导航'}
                width={'800px'}
                onCancel={() => {
                    this.setState({
                        modalShow: false,
                    });
                }}
                onOk={() => {
                    this.props.onSave?.([
                        ...this.state.fixedList,
                        ...this.state.sortList,
                    ]);
                }}
                maskClosable={false}
            >
                <div className={css.customTopMenuContent}>
                    {this.renderLeftMenuBox()}
                    {this.renderRightMenuBox()}
                </div>
            </Modal>
        );
    }
}
